import * as React from "react";
import axios from "axios";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { push } from "connected-react-router";
// components
import { DIALOG_TYPES } from "common/components/fullscreenDialog";
import { IAppState } from "app/rootReducer";
import { AppDispatch } from "app/store";
import {
  followThisUser,
  getUserFollowees,
  getUserFollowers,
  getUserBoosters,
  changeUserInformation,
} from "@vingle/wtf-sdk/actions/userProfile";
import {
  ActionCreators as RelationActionCreators,
  getBlockedStatus,
} from "@vingle/wtf-sdk/actions/relation";
import { ActionCreators as DialogActionCreators } from "app/vingleDialog/actions";
import { changeUserFollowing } from "@vingle/wtf-sdk/actions/common";
import { paginatedUsersDenormalizer } from "@vingle/wtf-sdk/models/denormalizer";
import VingleURL from "@vingle/wtf-sdk/url";
import {
  blockedStatusSelector,
  profileUserSelector,
} from "@vingle/wtf-sdk/selectors/userProfile";
import ProfileDefaultHeaderComponent from "./component";

function mapDispatchToProps(dispatch: AppDispatch) {
  return bindActionCreators(
    {
      followThisUser,
      changeUserFollowing,
      openSignUpDialog: DialogActionCreators.openSignUpDialog,
      getUserFollowees,
      getUserFollowers,
      getUserBoosters,
      openBlockDialog: RelationActionCreators.openBlockDialog,
      changeUserInformation,
      getBlockedStatus,
      push,
    },
    dispatch,
  );
}

function mapStateToProps(state: IAppState) {
  const entities = state.entities;
  return {
    currentUser: state.app.currentUser,
    userProfile: {
      ...state.userProfile.profile,
      profile: profileUserSelector(state),
    },
    isLoading: state.userProfile.profile.isLoading,
    scoreState: {
      followees: paginatedUsersDenormalizer(
        state.userProfile.scoreState.followees,
        entities,
      ),
      followers: paginatedUsersDenormalizer(
        state.userProfile.scoreState.followers,
        entities,
      ),
      boosters: paginatedUsersDenormalizer(
        state.userProfile.scoreState.boosters,
        entities,
      ),
    },
    blockedStatus: blockedStatusSelector(state),
  };
}

interface IProps
  extends ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {}

class ProfileDefaultHeaderContainer extends React.PureComponent<IProps> {
  private readonly cancelTokenSource = axios.CancelToken.source();

  public componentWillUnmount() {
    this.cancelTokenSource.cancel();
  }

  public componentDidMount(): void {
    if (
      !this.isMine() &&
      this.props.currentUser &&
      this.props.userProfile.profile
    ) {
      this.props.getBlockedStatus({
        username: this.props.userProfile.profile.username,
        cancelToken: this.cancelTokenSource.token,
      });
    }
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (
      !this.isMine() &&
      prevProps.userProfile.profile &&
      this.props.userProfile.profile &&
      this.props.currentUser &&
      prevProps.userProfile.profile.username !==
        this.props.userProfile.profile.username
    ) {
      this.props.getBlockedStatus({
        username: this.props.userProfile.profile.username,
        cancelToken: this.cancelTokenSource.token,
      });
    }
  }

  public render() {
    const { currentUser, userProfile, scoreState, blockedStatus } = this.props;
    return (
      <ProfileDefaultHeaderComponent
        isMine={this.isMine()}
        user={userProfile.profile}
        userProfile={userProfile}
        currentUser={currentUser}
        scoreState={scoreState}
        blockedStatus={blockedStatus}
        onDMClick={this.handleDMButtonClick}
        onFollowThisProfileUser={this.handleFollowThisUser}
        onUnFollowThisProfileUser={this.handleUnFollowThisUser}
        onFollowUserOfList={this.handleUserFollowClick}
        onFullScreenInfiniteLoad={this.handleFullScreenInfiniteLoad}
        onInitialUserListLoad={this.handleInitialUserListLoad}
        onSetUserBlockStatus={this.handleSetUserBlockStatus}
        onChangeUserProfile={this.handleChangeUserProfile}
      />
    );
  }

  private readonly isMine = () => {
    const { currentUser, userProfile } = this.props;
    return Boolean(
      (currentUser && currentUser.id) ===
        (userProfile.profile && userProfile.profile.id),
    );
  };

  private readonly handleChangeUserProfile = (params: {
    updateFields: Vingle.User.IUserForm;
    successMessage?: string;
    profile_image?: File | Blob | null;
    cover_image?: File | Blob;
  }) => {
    this.props.changeUserInformation(params);
  };

  private readonly handleSetUserBlockStatus = (
    username: string,
    level: Vingle.Enums.UserBlockLevel,
  ) => {
    this.props.openBlockDialog({
      username,
      level,
    });
  };

  private readonly handleDMButtonClick = () => {
    const { userProfile, push } = this.props;
    if (userProfile.profile) {
      push(
        new VingleURL.UserDMList({
          username: userProfile.profile.username,
        }).toString(),
      );
    }
  };

  private readonly handleInitialUserListLoad = (type: DIALOG_TYPES) => {
    const { userProfile, scoreState } = this.props;
    const profile = userProfile.profile;
    if (profile) {
      let pagination;
      switch (type) {
        case DIALOG_TYPES.FOLLOWEE: {
          pagination = scoreState.followees;
          break;
        }
        case DIALOG_TYPES.FOLLOWER: {
          pagination = scoreState.followers;
          break;
        }
        case DIALOG_TYPES.BOOSTER: {
          pagination = scoreState.boosters;
          break;
        }
      }
      if (
        pagination &&
        !(
          pagination.data.length ||
          pagination.paging.hasOwnProperty("after") ||
          pagination.paging.hasOwnProperty("before")
        )
      ) {
        this.handleUserListLoad(type);
      }
    }
  };

  private readonly handleFollowThisUser = () => {
    const { userProfile } = this.props;
    if (userProfile.profile) {
      const profile = userProfile.profile;
      this.props.followThisUser({
        username: profile.username,
        following: true,
        cancelToken: this.cancelTokenSource.token,
      });
    }
  };

  private readonly handleUnFollowThisUser = () => {
    const { userProfile } = this.props;
    if (userProfile.profile) {
      const profile = userProfile.profile;
      this.props.followThisUser({
        username: profile.username,
        following: false,
        cancelToken: this.cancelTokenSource.token,
      });
    }
  };

  private readonly handleUserFollowClick = (user: Vingle.User.IListUser) => {
    const { currentUser } = this.props;
    if (currentUser && user) {
      this.props.changeUserFollowing({
        userId: user.id,
        username: user.username,
        followingState: !(user.relation && user.relation.following),
        cancelToken: this.cancelTokenSource.token,
      });
    } else {
      this.props.openSignUpDialog();
    }
  };

  private readonly handleUserListLoad = (type: DIALOG_TYPES) => {
    const { userProfile, isLoading, scoreState } = this.props;
    const profile = userProfile.profile;
    if (isLoading) {
      return null;
    }
    if (profile) {
      switch (type) {
        case DIALOG_TYPES.FOLLOWEE: {
          const followees = scoreState.followees;
          this.props.getUserFollowees({
            username: profile.username,
            options: {
              count: 10,
              after: followees.paging.after,
            },
            cancelToken: this.cancelTokenSource.token,
          });
          break;
        }
        case DIALOG_TYPES.FOLLOWER: {
          const followers = scoreState.followers;
          this.props.getUserFollowers({
            username: profile.username,
            options: {
              count: 10,
              after: followers.paging.after,
            },
            cancelToken: this.cancelTokenSource.token,
          });
          break;
        }
        case DIALOG_TYPES.BOOSTER: {
          const boosters = scoreState.boosters;
          this.props.getUserBoosters({
            username: profile.username,
            options: {
              count: 10,
              after: boosters.paging.after,
            },
            cancelToken: this.cancelTokenSource.token,
          });
          break;
        }
      }
    }
  };

  private readonly handleFullScreenInfiniteLoad = (type: DIALOG_TYPES) => {
    const { userProfile, scoreState } = this.props;
    const profile = userProfile.profile;
    if (profile) {
      let scorePaging;
      switch (type) {
        case DIALOG_TYPES.FOLLOWEE: {
          scorePaging = scoreState.followees.paging;
          break;
        }
        case DIALOG_TYPES.FOLLOWER: {
          scorePaging = scoreState.followers.paging;
          break;
        }
        case DIALOG_TYPES.BOOSTER: {
          scorePaging = scoreState.boosters.paging;
          break;
        }
      }
      if (scorePaging && scorePaging.after) {
        this.handleUserListLoad(type);
      }
    }
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ProfileDefaultHeaderContainer);
